import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createIntegrator,
  createIntegratorSession,
  deleteIntegrator,
  listAdminIntegrators,
  listIntegrators,
  updateIntegrator,
} from './api';
import { integratorsKeys } from './query-keys';
import type {
  IntegratorAdmin,
  IntegratorAdminList,
  IntegratorCreate,
  IntegratorPublicList,
  IntegratorSession,
  IntegratorUpdate,
} from './schemas';
import type { ApiError } from '@/lib/api-error';
import { toast } from '@/lib/toast';

// User-facing lista de integradores. Alimenta el sidebar dinámico. Sin
// polling — cache 5min. Cambios en el admin invalidan.
export function useIntegrators() {
  return useQuery<IntegratorPublicList, ApiError>({
    queryKey: integratorsKeys.publicList(),
    queryFn: () => listIntegrators(),
    staleTime: 5 * 60 * 1000,
  });
}

// Admin list. Gate `integrations:manage` en el back; el front no lo dispara
// desde este hook — se llama desde la admin page ya gated.
export function useAdminIntegrators() {
  return useQuery<IntegratorAdminList, ApiError>({
    queryKey: integratorsKeys.adminList(),
    queryFn: () => listAdminIntegrators(),
  });
}

export function useCreateIntegrator() {
  const qc = useQueryClient();
  return useMutation<IntegratorAdmin, ApiError, IntegratorCreate>({
    mutationFn: (body) => createIntegrator(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: integratorsKeys.all });
      toast.success('Integrador creado');
    },
    onError: (err) => toast.apiError('No se pudo crear el integrador', err),
  });
}

export function useUpdateIntegrator() {
  const qc = useQueryClient();
  return useMutation<
    IntegratorAdmin,
    ApiError,
    { id: string; body: IntegratorUpdate }
  >({
    mutationFn: ({ id, body }) => updateIntegrator(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: integratorsKeys.all });
      toast.success('Integrador actualizado');
    },
    onError: (err) =>
      toast.apiError('No se pudo actualizar el integrador', err),
  });
}

export function useDeleteIntegrator() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => deleteIntegrator(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: integratorsKeys.all });
      toast.success('Integrador eliminado');
    },
    onError: (err) => toast.apiError('No se pudo eliminar el integrador', err),
  });
}

// Session mutation — one-shot ticket. Se dispara desde IntegratorFrame al
// mount. No cachea (single-use).
export function useCreateIntegratorSession() {
  return useMutation<IntegratorSession, ApiError, string>({
    mutationFn: (slug) => createIntegratorSession(slug),
  });
}
